import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { PresenceProvider } from "@/contexts/PresenceProvider";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LocaleRouter } from "@/components/LocaleRouter";
import { LocaleRedirect } from "@/components/LocaleRedirect";
import { CookieBanner } from "./components/CookieBanner";
import { EpRewardOverlay } from "./components/EpRewardOverlay";
import { ActivityTrackerInit } from "./components/ActivityTrackerInit";
import { AnalyticsTracker } from "./components/AnalyticsTracker";
import { MetaPixelTracker } from "./components/MetaPixelTracker";
import { MetaPixelRegistrationTracker } from "./components/MetaPixelRegistrationTracker";
import { RegistrationTrackingSync } from "./components/RegistrationTrackingSync";

import { CheckoutReturnNotice } from "./components/CheckoutReturnNotice";
import TrefferbildHinweis from "@/components/TrefferbildHinweis";


// Landing is eagerly loaded (it's the entry route / LCP page)
import Landing from "./pages/Landing";

// Lazy-loaded pages (code-split into separate chunks)
const Index = lazy(() => import("./pages/Index"));
const LockedSection = lazy(() => import("./components/LockedSection"));
const Liga = lazy(() => import("./pages/Liga"));
const Profile = lazy(() => import("./pages/Profile"));
const Statistics = lazy(() => import("./pages/Statistics"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Friends = lazy(() => import("./pages/Friends"));
const LocalGames = lazy(() => import("./pages/LocalGames"));
const Auszeichnungen = lazy(() => import("./pages/Auszeichnungen"));
const BoardManager = lazy(() => import("./pages/BoardManager"));

const MeineTrainings = lazy(() => import("./pages/MeineTrainings"));
const Analyse = lazy(() => import("./pages/Analyse"));

const NotFound = lazy(() => import("./pages/NotFound"));
const CoachSession = lazy(() => import("./pages/CoachSession"));
const TrainingPlanen = lazy(() => import("./pages/TrainingPlanen"));
const Wochenplan = lazy(() => import("./pages/Wochenplan"));

const Inbox = lazy(() => import("./pages/Inbox"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminUsersPage = lazy(() => import("./pages/admin/AdminUsersPage"));
const AdminUserDetailPage = lazy(() => import("./pages/admin/AdminUserDetailPage"));
const AdminRegistrationsPage = lazy(() => import("./pages/admin/AdminRegistrationsPage"));
const AdminTrainingsPage = lazy(() => import("./pages/admin/AdminTrainingsPage"));
const AdminAnalyticsPage = lazy(() => import("./pages/admin/AdminAnalyticsPage"));
const AdminMessagesPage = lazy(() => import("./pages/admin/AdminMessagesPage"));
const AdminChatPage = lazy(() => import("./pages/admin/AdminChatPage"));
const AdminWochenchallengePage = lazy(() => import("./pages/admin/AdminWochenchallengePage"));
const AdminSettingsPage = lazy(() => import("./pages/admin/AdminSettingsPage"));
const AdminRechnerPage = lazy(() => import("./pages/admin/AdminRechnerPage"));
const AdminLocalGamesPage = lazy(() => import("./pages/admin/AdminLocalGamesPage"));
const AdminTrackingPage = lazy(() => import("./pages/admin/AdminTrackingPage"));
const AdminReviewsPage = lazy(() => import("./pages/admin/AdminReviewsPage"));
const AdminFeedbackPage = lazy(() => import("./pages/admin/AdminFeedbackPage"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const Nutzungsbedingungen = lazy(() => import("./pages/Nutzungsbedingungen"));
const AGB = lazy(() => import("./pages/AGB"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const FAQ = lazy(() => import("./pages/FAQ"));
const UeberUns = lazy(() => import("./pages/UeberUns"));
const DartsTraining = lazy(() => import("./pages/DartsTraining"));
const DartWissen = lazy(() => import("./pages/DartWissen"));
const DartTrainingAnfaenger = lazy(() => import("./pages/DartTrainingAnfaenger"));
const DartTrainingsplan = lazy(() => import("./pages/DartTrainingsplan"));
const DartAverageVerbessern = lazy(() => import("./pages/DartAverageVerbessern"));
const DartScoringVerbessern = lazy(() => import("./pages/DartScoringVerbessern"));
const DartDoppeltraining = lazy(() => import("./pages/DartDoppeltraining"));
const DartCheckoutTraining = lazy(() => import("./pages/DartCheckoutTraining"));
const Widerruf = lazy(() => import("./pages/Widerruf"));
const VertragWiderrufen = lazy(() => import("./pages/VertragWiderrufen"));
const Pro = lazy(() => import("./pages/Pro"));
// Dev-only test harness for the new controlled game libraries (Step 1).
const GamesPreview = lazy(() => import("./pages/GamesPreview"));

const CoachProvider = lazy(() => import("./contexts/CoachProvider").then(m => ({ default: m.CoachProvider })));

// Globale Defaults: moderates Caching statt "immer sofort veraltet".
// Bereiche, die quasi-live sein müssen (Wochenchallenge, Chat, Präsenz,
// Ranglisten), setzen weiterhin lokal eigene staleTime/refetchInterval-Werte
// und werden davon nicht eingeschränkt.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});


/** Wrap a page with the sidebar dashboard layout */
const withDashboard = (page: React.ReactNode) => (
  <DashboardLayout>{page}</DashboardLayout>
);

/** All app routes (reused for both root and locale-prefixed paths) */
const appRoutes = (
  <>
    <Route index element={<Landing />} />
    <Route path="startseite" element={withDashboard(<Index />)} />
    <Route path="profile" element={withDashboard(<Profile />)} />
    <Route path="statistics" element={withDashboard(<Statistics />)} />
    <Route path="reset-password" element={<ResetPassword />} />
    <Route path="friends" element={withDashboard(<Friends />)} />
    <Route path="lokale-spiele" element={withDashboard(<LocalGames />)} />
    <Route path="auszeichnungen" element={withDashboard(<Auszeichnungen />)} />
    <Route path="board-manager" element={withDashboard(<BoardManager />)} />
    
    <Route path="liga" element={withDashboard(<Liga />)} />
    <Route path="training" element={<Navigate to="/meine-trainings" replace />} />
    <Route path="analyse" element={withDashboard(<Analyse />)} />
    
    <Route path="meine-trainings" element={withDashboard(<MeineTrainings />)} />
    <Route path="coach-session" element={<CoachProvider><CoachSession /></CoachProvider>} />
    <Route path="training-planen" element={withDashboard(<TrainingPlanen />)} />
    <Route path="mein-wochenplan" element={withDashboard(<Wochenplan />)} />
    <Route path="wochenchallenge" element={withDashboard(<LockedSection section="challenge" />)} />
    <Route path="wochenchallenge/spielen" element={withDashboard(<LockedSection section="challenge" />)} />



    <Route path="inbox" element={withDashboard(<Inbox />)} />
    <Route path="admin" element={<AdminDashboard />} />
    <Route path="admin/users" element={<AdminUsersPage />} />
    <Route path="admin/users/:userId" element={<AdminUserDetailPage />} />
    <Route path="admin/registrierungen" element={<AdminRegistrationsPage />} />

    <Route path="admin/trainings" element={<AdminTrainingsPage />} />
    <Route path="admin/analytics" element={<AdminAnalyticsPage />} />
    <Route path="admin/messages" element={<AdminMessagesPage />} />
    <Route path="admin/chat" element={<AdminChatPage />} />
    <Route path="admin/feedback" element={<AdminFeedbackPage />} />
    <Route path="admin/wochenchallenge" element={<AdminWochenchallengePage />} />


    <Route path="admin/settings" element={<AdminSettingsPage />} />
    <Route path="admin/rechner" element={<AdminRechnerPage />} />
    <Route path="admin/local-games" element={<AdminLocalGamesPage />} />
    <Route path="admin/tracking" element={<AdminTrackingPage />} />
    <Route path="admin/reviews" element={<AdminReviewsPage />} />
    <Route path="impressum" element={<Impressum />} />
    <Route path="datenschutz" element={<Datenschutz />} />
    <Route path="nutzungsbedingungen" element={<Nutzungsbedingungen />} />
    <Route path="agb" element={<AGB />} />
    <Route path="kontakt" element={<Kontakt />} />
    <Route path="faq" element={<FAQ />} />
    <Route path="ueber-uns" element={<UeberUns />} />
    <Route path="darts-training" element={<DartsTraining />} />
    <Route path="dart-wissen" element={<DartWissen />} />
    <Route path="dart-wissen/dart-training-anfaenger" element={<DartTrainingAnfaenger />} />
    <Route path="dart-wissen/dart-trainingsplan" element={<DartTrainingsplan />} />
    <Route path="dart-wissen/dart-average-verbessern" element={<DartAverageVerbessern />} />
    <Route path="dart-wissen/dart-scoring-verbessern" element={<DartScoringVerbessern />} />
    <Route path="dart-wissen/dart-doppeltraining" element={<DartDoppeltraining />} />
    <Route path="dart-wissen/dart-checkout-training" element={<DartCheckoutTraining />} />


    <Route path="widerruf" element={<Widerruf />} />
    <Route path="vertrag-widerrufen" element={<VertragWiderrufen />} />
    <Route path="pro" element={withDashboard(<Pro />)} />
  </>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <PresenceProvider>
      <LanguageProvider>
      <TooltipProvider>
        <div className="pointer-events-none fixed inset-0 z-[110]">
          <Toaster />
          <Sonner />
        </div>
        <EpRewardOverlay />
        <ActivityTrackerInit />
        <BrowserRouter>
          <AnalyticsTracker />
          <MetaPixelTracker />
          <MetaPixelRegistrationTracker />
          <RegistrationTrackingSync />

          <CheckoutReturnNotice />
          <CookieBanner />

          <Suspense fallback={<div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',background:'#0a0a0f'}}><svg width="48" height="48" viewBox="0 0 24 24" style={{animation:'spin 1s linear infinite',color:'#6366f1'}}><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round"/></svg></div>}>
          <Routes>
            {/* Locale-prefixed routes: /en/… or /de/… */}
            <Route path=":locale" element={<LocaleRouter />}>
              {appRoutes}
            </Route>

            {/* Root routes without locale – redirect to /{lang}/… */}
            <Route path="/" element={<LocaleRedirect />} />
            <Route path="/startseite" element={<LocaleRedirect />} />
            <Route path="/profile" element={<LocaleRedirect />} />
            <Route path="/statistics" element={<LocaleRedirect />} />
            <Route path="/reset-password" element={<LocaleRedirect />} />
            <Route path="/friends" element={<LocaleRedirect />} />
            <Route path="/auszeichnungen" element={<LocaleRedirect />} />
            <Route path="/board-manager" element={<LocaleRedirect />} />
            <Route path="/liga" element={<LocaleRedirect />} />
            <Route path="/training" element={<LocaleRedirect />} />
            <Route path="/analyse" element={<LocaleRedirect />} />
            
            <Route path="/meine-trainings" element={<LocaleRedirect />} />
            <Route path="/coach-session" element={<LocaleRedirect />} />
            <Route path="/training-planen" element={<LocaleRedirect />} />
            <Route path="/mein-wochenplan" element={<LocaleRedirect />} />
            <Route path="/wochenchallenge" element={<LocaleRedirect />} />
            <Route path="/wochenchallenge/spielen" element={<LocaleRedirect />} />



            <Route path="/inbox" element={<LocaleRedirect />} />
            <Route path="/admin" element={<LocaleRedirect />} />
            <Route path="/admin/users" element={<LocaleRedirect />} />
            <Route path="/admin/users/:userId" element={<LocaleRedirect />} />
            <Route path="/admin/registrierungen" element={<LocaleRedirect />} />

            <Route path="/admin/trainings" element={<LocaleRedirect />} />
            <Route path="/admin/analytics" element={<LocaleRedirect />} />
            <Route path="/admin/messages" element={<LocaleRedirect />} />
            <Route path="/admin/chat" element={<LocaleRedirect />} />
            <Route path="/admin/feedback" element={<LocaleRedirect />} />
            <Route path="/admin/wochenchallenge" element={<LocaleRedirect />} />


            <Route path="/admin/settings" element={<LocaleRedirect />} />
            <Route path="/admin/rechner" element={<LocaleRedirect />} />
            <Route path="/admin/local-games" element={<LocaleRedirect />} />
            <Route path="/admin/tracking" element={<LocaleRedirect />} />
            <Route path="/admin/reviews" element={<LocaleRedirect />} />
            <Route path="/impressum" element={<LocaleRedirect />} />
            <Route path="/datenschutz" element={<LocaleRedirect />} />
            <Route path="/nutzungsbedingungen" element={<LocaleRedirect />} />
            <Route path="/agb" element={<LocaleRedirect />} />
            <Route path="/kontakt" element={<LocaleRedirect />} />
            <Route path="/faq" element={<LocaleRedirect />} />
            <Route path="/ueber-uns" element={<LocaleRedirect />} />
            <Route path="/darts-training" element={<LocaleRedirect />} />
            <Route path="/dart-wissen" element={<LocaleRedirect />} />
            <Route path="/dart-wissen/dart-training-anfaenger" element={<LocaleRedirect />} />
            <Route path="/dart-wissen/dart-trainingsplan" element={<LocaleRedirect />} />
            <Route path="/dart-wissen/dart-average-verbessern" element={<LocaleRedirect />} />
            <Route path="/dart-wissen/dart-scoring-verbessern" element={<LocaleRedirect />} />
            <Route path="/dart-wissen/dart-doppeltraining" element={<LocaleRedirect />} />
            <Route path="/dart-wissen/dart-checkout-training" element={<LocaleRedirect />} />


            <Route path="/widerruf" element={<LocaleRedirect />} />
            <Route path="/vertrag-widerrufen" element={<LocaleRedirect />} />
            <Route path="/pro" element={<LocaleRedirect />} />

            {/* Dev-only games preview harness — not linked from any menu */}
            <Route path="/__games-preview" element={<GamesPreview />} />

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <TrefferbildHinweis />
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
      </LanguageProvider>
      </PresenceProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
